<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
    type: {
        type: String,
        required: false,
        default: 'info',
        validator: (value) => ['error', 'success', 'info', 'warning'].includes(value)
    },
    title: {
        type: String,
        required: false,
        default: null
    },
    message: {
        type: String,
        required: true
    },
    timeout: {
        type: Number,
        required: false,
        default: 5000
    }
});

const showSnackbar = ref(false);

const closeSnackbar = () => {
    showSnackbar.value = false;
};

const openSnackbar = () => {
    showSnackbar.value = true;
};

defineExpose({
    showSnackbar,
    closeSnackbar,
    openSnackbar
});

// Computed background color based on type
const backgroundColor = computed(() => {
    const colorMap = {
        success: '#CEDBD1',
        error: 'error',
        info: 'info',
        warning: 'warning'
    };
    return colorMap[props.type] || 'info';
});

// Computed title - use default if not provided
const displayTitle = computed(() => {
    if (props.title) {
        return props.title;
    }
    const titleMap = {
        success: 'Uspešno',
        error: 'Napaka',
        info: 'Informacija',
        warning: 'Opozorilo'
    };
    return titleMap[props.type] || 'Informacija';
});

// SVG icon paths for each type (Heroicons outline style)
const iconPath = computed(() => {
    const icons = {
        success: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
        error: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z',
        info: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
        warning: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
    };
    return icons[props.type] || icons.info;
});

</script>

<template>  
    <v-snackbar
        v-model="showSnackbar"
        :color="backgroundColor"
        :timeout="timeout"
        location="bottom"
        :class="`snackbar-type-${type}`"
    >
        <div class="flex items-start gap-3 w-full snackbar-content text-iron-grey">
            <!-- Left side: SVG Icon -->
            <div class="flex-shrink-0 snackbar-icon">
                <svg 
                    class="w-6 h-6" 
                    fill="none" 
                    stroke="currentColor" 
                    stroke-width="2" 
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path 
                        stroke-linecap="round" 
                        stroke-linejoin="round" 
                        :d="iconPath"
                    />
                </svg>
            </div>

            <!-- Right side: Title & Message -->
            <div class="flex-1 min-w-0 snackbar-text">
                <div class="font-semibold text-base snackbar-title">
                    {{ displayTitle }}
                </div>
                <div class="text-sm mt-1 snackbar-message">
                    {{ message }}
                </div>
            </div>
        </div>

        <template v-slot:actions>
            <button
                @click="closeSnackbar"
                class="snackbar-close-btn text-sm font-semibold px-4 py-2 rounded-xl border-2 transition-all"
            >
                Zapri
            </button>
        </template>
    </v-snackbar>
</template>

<style scoped>
/* Base content layout */
.snackbar-content {
    padding: 0.5rem 0;
}

/* Button styling matching theme design */
.snackbar-close-btn {
    min-width: auto;
    margin-left: 0.5rem;
    font-weight: 600;
}

.snackbar-close-btn:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
    border-radius: 0.75rem;
}

/* Success type - theme-green-dark background with iron-grey text */
:deep(.snackbar-type-success .v-snackbar__wrapper) {
    background-color: #B8C8BC !important;
    color: #444E4B !important;
}

:deep(.snackbar-type-success .snackbar-icon svg),
:deep(.snackbar-type-success .snackbar-title),
:deep(.snackbar-type-success .snackbar-message) {
    color: #444E4B !important;
}

:deep(.snackbar-type-success .snackbar-close-btn) {
    background-color: #FFFFFF !important;
    color: #444E4B !important;
    border-color: #b4b4b2 !important;
}

:deep(.snackbar-type-success .snackbar-close-btn:hover) {
    background-color: #F1F0E6 !important;
    border-color: #444E4B !important;
}

/* Error type - red background with white text (keeping red for errors) */
:deep(.snackbar-type-error .v-snackbar__wrapper) {
    background-color: rgb(244, 67, 54) !important;
    color: rgb(255, 255, 255) !important;
}

:deep(.snackbar-type-error .snackbar-icon svg),
:deep(.snackbar-type-error .snackbar-title),
:deep(.snackbar-type-error .snackbar-message) {
    color: rgb(255, 255, 255) !important;
}

:deep(.snackbar-type-error .snackbar-close-btn) {
    background-color: #FFFFFF !important;
    color: #444E4B !important;
    border-color: rgba(255, 255, 255, 0.5) !important;
}

:deep(.snackbar-type-error .snackbar-close-btn:hover) {
    background-color: #F1F0E6 !important;
    border-color: #FFFFFF !important;
}

/* Info type - iron-grey background with white text */
:deep(.snackbar-type-info .v-snackbar__wrapper) {
    background-color: #444E4B !important;
    color: rgb(255, 255, 255) !important;
}

:deep(.snackbar-type-info .snackbar-icon svg),
:deep(.snackbar-type-info .snackbar-title),
:deep(.snackbar-type-info .snackbar-message) {
    color: rgb(255, 255, 255) !important;
}

:deep(.snackbar-type-info .snackbar-close-btn) {
    background-color: #FFFFFF !important;
    color: #444E4B !important;
    border-color: rgba(255, 255, 255, 0.5) !important;
}

:deep(.snackbar-type-info .snackbar-close-btn:hover) {
    background-color: #F1F0E6 !important;
    border-color: #FFFFFF !important;
}

/* Warning type - linen-dark background with iron-grey text */
:deep(.snackbar-type-warning .v-snackbar__wrapper) {
    background-color: #D8D0BF !important;
    color: #444E4B !important;
}

:deep(.snackbar-type-warning .snackbar-icon svg),
:deep(.snackbar-type-warning .snackbar-title),
:deep(.snackbar-type-warning .snackbar-message) {
    color: #444E4B !important;
}

:deep(.snackbar-type-warning .snackbar-close-btn) {
    background-color: #FFFFFF !important;
    color: #444E4B !important;
    border-color: #b4b4b2 !important;
}

:deep(.snackbar-type-warning .snackbar-close-btn:hover) {
    background-color: #F1F0E6 !important;
    border-color: #444E4B !important;
}

/* Ensure proper spacing and alignment */
:deep(.v-snackbar__content) {
    display: flex;
    align-items: center;
    width: 100%;
}

:deep(.v-snackbar__wrapper) {
    padding: 1rem 1.25rem;
}
</style>

